import React, { useRef } from "react";
import { Button } from "react-bootstrap";
import { ActionHandlerFunction, Sort } from "@preveil-api";
import {
  Account, SearchNode, DriveUIActionTypes, SearchProgressValues, SearchFieldType, getOppositeSortDirection, SORT_DIRECTION, SearchErrorCodes, MessageAnchors, DriveSearchMessages,
  PathInfo, NodePathInfoMap
} from "src/common";
import { Icon, Loading } from "src/components";
import { SearchRow } from ".";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  search_term?: string;
  results?: SearchNode[];
  error?: SearchErrorCodes;
  sort: Sort<keyof SearchNode>;
  progress: number;
  paused: boolean;
  searching: boolean;
  handleAction: ActionHandlerFunction;
}

function SearchResultsComponent(props: AllProps) {
  const { current_account, search_term, results, paused, searching, sort, progress, error, handleAction } = props;
  // Description: Maintainer nodes fetched grants
  const path_list_ref = useRef<NodePathInfoMap>(new Map<string, PathInfo[]>);
  function setPathListRef(id: string, _path_list_ref: PathInfo[]) {
    path_list_ref.current.set(id, _path_list_ref);
  }


  // Description: handle Sort only when searching is finished
  function handleSort(field: keyof SearchNode) {
    !searching &&
      handleAction({
        actionType: DriveUIActionTypes.SetSort,
        params: {
          field,
          direction: sort.field === field ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending
        }
      });
  }

  // Description: Error Message block
  function ErrorMessage() {
    return <div className="empty">
      <p>  <Icon className="ficon-alert-octagon me-2" />
        {DriveSearchMessages.default}
      </p>
      <Button
        variant="no-outline-primary"
        onClick={() => handleAction({ actionType: DriveUIActionTypes.Reset, params: { error } })} >Try Again</Button>
    </div>;
  }

  // Description: Paused Message block
  function PausedMessage() {
    return <div className="empty">
      <p>
        <Icon className="ficon-alert-octagon me-2" />
        {DriveSearchMessages.add_search_term_warning}
      </p>
      {
        !!search_term &&
        <Button variant="no-outline-primary"
          onClick={() => handleAction({ actionType: DriveUIActionTypes.Pause, params: false })}>Continue Searching</Button>
      }
    </div>;
  }

  // Description: Empty message after loading all data
  function EmptyMessage() {
    return (!!search_term && !results) ? <Loading className="in-place" message="searching..." /> :
      <div className="empty">
        <Icon className="ficon-alert-octagon me-2" />
        {!!search_term ? DriveSearchMessages.default_search_term.replace(MessageAnchors.search_term, search_term) :
          DriveSearchMessages.add_search_term_warning}
      </div>;
  }


  function RenderView() {
    return <ul>
      <li className={`results-header ${searching ? " disabled" : ""}`}>
        <div onClick={() => handleSort(SearchFieldType.name)}>
          Name
          {
            sort.field === SearchFieldType.name &&
            <Icon className={`${sort.direction === SORT_DIRECTION.descending ? "ficon-arrow-down" : "ficon-arrow-up"}`} />
          }
        </div>
        <div onClick={() => handleSort(SearchFieldType.last_modification_date)}>
          Modified
          {
            sort.field === SearchFieldType.last_modification_date &&
            <Icon className={`${sort.direction === SORT_DIRECTION.descending ? "ficon-arrow-down" : "ficon-arrow-up"}`} />
          }
        </div>
      </li>
      <>
        {
          _.map(results, (search_node: SearchNode, i: number) => {
            return <SearchRow
              key={`row_${i}`}
              current_account={current_account}
              search_node={search_node}
              path_info={path_list_ref.current.get(search_node.id)}
              handleAction={handleAction}
              handleRowAction={(id: string, path_info: PathInfo[]) => setPathListRef(id, path_info)} />;
          })
        }
      </>
    </ul>;
  }

  // Description: Render the result section depending on search term field, progress, error, or pause states.
  function RenderResultState() {
    if (!!results && results.length) {
      return RenderView();
    } else {
      if (progress === SearchProgressValues.complete) {
        return <EmptyMessage />;
      } else if (!!error) {
        return <ErrorMessage />;
      } else if (paused) {
        return !!search_term ? <EmptyMessage /> : <PausedMessage />;
      } else {
        return <Loading className="in-place" message="searching..." />;
      }
    }
  }

  return RenderResultState();
}

export default React.memo(SearchResultsComponent);
